/**
 * F01 — deletion outcomes.
 *
 * A delete has two independent parts: the local row and any remote copies.
 * They succeed and fail separately, so they are reported separately and a row
 * is only ever removed from the list once the server confirmed the local
 * deletion. A rejected request leaves the row on screen.
 *
 * Server contract (DELETE /api/articles/<id>):
 *   200 -> {success: true, remote: {<dest>: "deleted" | "error …" | "skipped …"}}
 *   non-2xx -> throws, with the reason in response.data.error
 */

const titleCase = (s) => s.charAt(0).toUpperCase() + s.slice(1);
const unique = (xs) => [...new Set(xs)];

/** Split a server `remote` block into removed / failed / skipped destinations. */
export function classifyRemote(remote) {
  const removed = [], failed = [], skipped = [];
  for (const [dest, v] of Object.entries(remote || {})) {
    if (v === 'deleted') removed.push(dest);
    else if (typeof v === 'string' && v.startsWith('error')) failed.push({ dest, message: v });
    else skipped.push(dest);
  }
  return { removed, failed, skipped };
}

/** The human-readable reason a delete request failed. */
export function deleteErrorMessage(e) {
  return e?.response?.data?.error || e?.message || 'failed';
}

/**
 * One document's outcome. `localDeleted` is true only when the server
 * confirmed it — this is what callers gate row removal on.
 */
export function classifyDeleteOutcome({ id, ok, data, error }) {
  return {
    id,
    localDeleted: Boolean(ok),
    error: ok ? null : (error || 'failed'),
    remote: classifyRemote(ok ? data?.remote : undefined),
  };
}

/** Aggregate outcomes, keeping local deletion and remote cleanup distinct. */
export function summarizeOutcomes(outcomes) {
  const deletedIds = [], failedIds = [], remoteRemoved = [], remoteFailed = [];
  const errors = {};
  for (const o of outcomes) {
    if (o.localDeleted) deletedIds.push(o.id); else { failedIds.push(o.id); errors[o.id] = o.error; }
    for (const dest of o.remote.removed) remoteRemoved.push({ id: o.id, dest });
    for (const f of o.remote.failed) remoteFailed.push({ id: o.id, dest: f.dest, message: f.message });
  }
  return {
    total: outcomes.length,
    deleted: deletedIds.length,
    failed: failedIds.length,
    deletedIds,
    failedIds,
    remoteRemoved,
    remoteFailed,
    errors,
  };
}

/**
 * A persistent summary. It never reports a count that wasn't confirmed, and
 * never claims a remote copy was removed when its cleanup failed.
 */
export function formatBatchSummary(summary) {
  const parts = [];
  if (summary.deleted > 0) parts.push(`${summary.deleted} deleted`);
  if (summary.failed > 0) parts.push(`${summary.failed} failed`);
  if (!parts.length) return 'Nothing was deleted';

  let text = parts.join(' · ');
  if (summary.remoteRemoved.length) {
    const dests = unique(summary.remoteRemoved.map(r => r.dest)).map(titleCase);
    text += ` · also removed from ${dests.join(' & ')}`;
  }
  if (summary.remoteFailed.length) {
    const dests = unique(summary.remoteFailed.map(r => r.dest)).map(titleCase);
    text += ` · still in ${dests.join(' & ')} — remote cleanup failed`;
  }
  return text;
}

/** Single-document message, same honesty rules as the batch summary. */
export function formatSingleOutcome(outcome) {
  if (!outcome.localDeleted) return `Not deleted — ${outcome.error}`;
  return formatBatchSummary(summarizeOutcomes([outcome]));
}

/**
 * Delete each id in turn and return one outcome per id. Sequential so that a
 * failure is attributable to a specific document. Never throws.
 */
export async function runDeleteBatch(ids, deleteOne) {
  const outcomes = [];
  for (const id of ids) {
    try {
      // eslint-disable-next-line no-await-in-loop
      const data = await deleteOne(id);
      outcomes.push(classifyDeleteOutcome({ id, ok: true, data }));
    } catch (e) {
      outcomes.push(classifyDeleteOutcome({ id, ok: false, error: deleteErrorMessage(e) }));
    }
  }
  return { outcomes, summary: summarizeOutcomes(outcomes) };
}
